"use client";

import { useEffect, useState } from "react";
import { Link } from '@/components/LocaleLink';
import { Tag } from "lucide-react";
import { useLocale } from "@/hooks/useLocale";
import { t } from "@/i18n/t";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "https://api.autofrance.se/api";

interface Campaign {
  name: string;
  discount_value: number;
  ends_at: string;
}

export function CampaignBanner() {
  const { locale } = useLocale();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    fetch(`${API_URL}/campaigns/active`)
      .then((r) => r.json())
      .then((data) => {
        const c = data.campaigns?.[0];
        if (c?.ends_at) {
          setCampaign({
            name: c.name || t(locale, 'campaign.defaultName'),
            discount_value: c.discount_value || 15,
            ends_at: c.ends_at,
          });
        }
      })
      .catch(() => {});
  }, [locale]);

  useEffect(() => {
    if (!campaign?.ends_at) return;
    const endDate = new Date(campaign.ends_at);
    const tick = () => {
      const diff = Math.max(0, endDate.getTime() - Date.now());
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
      });
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [campaign?.ends_at]);

  if (!mounted || !campaign) return null;

  const countdown = timeLeft.days > 0
    ? t(locale, 'campaign.timeLeftDays', { days: timeLeft.days, hours: timeLeft.hours })
    : t(locale, 'campaign.timeLeftHours', { hours: timeLeft.hours, minutes: timeLeft.minutes });

  return (
    <Link
      href="/erbjudanden"
      className="block bg-gradient-to-r from-red-600 via-red-700 to-red-800 text-white hover:from-red-700 hover:via-red-800 hover:to-red-900 transition-colors"
    >
      <div className="container mx-auto px-4 py-2.5 flex items-center justify-center gap-3 text-sm">
        <Tag className="h-3.5 w-3.5 shrink-0" />
        <span className="font-bold">
          {campaign.name} &mdash; {t(locale, 'campaign.discount', { value: campaign.discount_value })}
        </span>
        <span className="hidden sm:inline text-white/70">|</span>
        <span className="hidden sm:inline text-white/90 font-medium">
          {countdown}
        </span>
        <span className="text-white/70 text-xs font-medium">
          {t(locale, 'campaign.viewDeals')} &rarr;
        </span>
      </div>
    </Link>
  );
}
